import Newsletter from "@/components/builder/newsletter";
import { CartIcon } from "@/components/icons/cart-icon";
import { LeftArrow } from "@/components/icons/left-arrow-icon";
import RightArrow from "@/components/icons/right-arrow-icon";
import { UserIcon } from "@/components/icons/user-icon";
import { XIcon } from "@/components/icons/x-icon";
import { type NewsletterTranslations } from "@/components/layout/footer/translations";
import { RawHeaderNavigationEntry } from "@/components/layout/header";
import {
  NavbarEntry,
  rootNavbarEntry,
  toNavbarEntries,
  useMobileMenu,
} from "@/components/layout/header/mobile-menu-context";
import { type NavbarTranslations } from "@/components/layout/header/translations";
import LocaleSwitcher from "@/components/layout/locale-switcher";
import getShopDomain from "@/util/get-shop-domain";
import { Locale } from "@/i18n/i18n.config";
import TranslatedLink from "@/libs/base/components/translated-link";
import { cn } from "@/libs/base/utils/layout";
import { useEffect } from "react";

interface PageProps {
  closeMenuDialog: () => void;
  selectLocale: (locale: Locale) => void;

  navbarTranslations: NavbarTranslations;
  newsletterTranslations: NewsletterTranslations;

  navigationEntries: RawHeaderNavigationEntry[];
  region: string;
  locale: Locale;
  selectableLocales: Locale[];
}

export default function MobileMenuDialog(props: PageProps) {
  const shopDomain = getShopDomain(props.region, props.locale);
  const navCtx = useMobileMenu();

  useEffect(() => {
    // Clear current root entries so they can be generated again and avoid duplicates.
    if (rootNavbarEntry.subElements) {
      rootNavbarEntry.subElements.clear();
    }
    navCtx.setAllPages(toNavbarEntries(props.navigationEntries));
  }, [props.navigationEntries]);

  const page = navCtx.currentPage;

  return (
    <>
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-5">
        {page.parent ? (
          <button type="button" className="outline-none" onClick={() => navCtx.up()}>
            <span className="sr-only">Navigate up</span>
            <LeftArrow
              width="20"
              height="20"
              aria-hidden="true"
              className="fill-current transition-all duration-300 hover:-translate-x-1 hover:text-gray-800"
            />
          </button>
        ) : (
          // Spacer so that flexbox spaces out the X button
          <div />
        )}

        <button
          type="button"
          className="outline-none transition-colors duration-300 hover:text-gray-800"
          onClick={() => props.closeMenuDialog()}
        >
          <span className="sr-only">Close menu</span>
          <XIcon width="20" height="20" aria-hidden="true" className="fill-current" />
        </button>
      </div>

      {page !== rootNavbarEntry ? (
        <TranslatedLink
          onClick={() => navCtx.close()}
          href={page.url}
          className="gap-4 border-b border-b-gray-200 bg-lightgray-200 px-4 py-4 text-headline underline transition-colors hover:bg-lightgray-300 tablet:text-subtitle"
        >
          {page.label}
        </TranslatedLink>
      ) : null}

      <div className="flex-1">
        {Array.from((navCtx.currentPage.subElements ?? new Map()).values()).map((entry) => (
          <EntryCmp entry={entry} key={entry.label} />
        ))}
      </div>

      <div className="p-5">
        <Newsletter translations={props.newsletterTranslations} />
      </div>

      <div className="flex w-full justify-between p-5">
        <div className="flex flex-col gap-y-[25px]">
          <a className="flex items-center gap-x-4" href={shopDomain + "/shop/"}>
            <CartIcon width={24} className="select-none" />
            <span className="select-none text-[16px]">{props.navbarTranslations.cart}</span>
          </a>
          <a className="flex items-center gap-x-4" href={shopDomain + "/customer/account/login/"}>
            <UserIcon width={24} className="select-none" />
            <span className="select-none text-[16px]">{props.navbarTranslations.account}</span>
          </a>
        </div>

        {/*Locale Switcher*/}
        <div className="self-end justify-self-end">
          <LocaleSwitcher
            locale={props.locale}
            selectableLocales={props.selectableLocales}
            selectLocale={props.selectLocale}
          />
        </div>
      </div>
    </>
  );
}

function EntryCmp({ entry }: { entry: NavbarEntry }) {
  const navCtx = useMobileMenu();
  const isSmall = navCtx.currentPage !== rootNavbarEntry;

  if (entry.subElements && entry.subElements.size > 0) {
    return (
      <button
        type="button"
        className={cn(
          "group flex w-full items-center gap-4 border-b border-b-gray-200 bg-lightgray-200 px-4 text-left transition-colors hover:bg-lightgray-300",
          isSmall ? "py-2.5 font-light" : "py-4 font-normal",
        )}
        onClick={() => navCtx.setCurrentPage(entry)}
      >
        <p className="flex-1 text-headline tablet:text-subtitle">{entry.label}</p>

        <RightArrow className="h-5 w-5 stroke-black stroke-[0.44] transition-transform group-hover:translate-x-1" />
      </button>
    );
  }

  return (
    <TranslatedLink
      onClick={() => navCtx.close()}
      href={entry.url}
      className={cn(
        "block items-center border-b border-b-gray-200 bg-lightgray-200 px-4 text-headline transition-colors hover:bg-lightgray-300 sm:text-subtitle",
        isSmall ? "py-2.5 font-light" : "py-4 font-normal",
      )}
    >
      {entry.label}
    </TranslatedLink>
  );
}
